/**
 * Open Dashboard — anonymous SAT progress store (specs/009-open-dashboard).
 *
 * Before signup, everything a visitor does (diagnostic + practice sets) lives
 * only in localStorage under `SAT_OPEN_PROGRESS_KEY`. There are no server rows
 * yet — this module is the single source of truth for that local state.
 *
 * Load/save/clear mechanics follow the guest-practice precedent (#237,
 * `satGuestPractice.ts`, now closed): JSON.parse in try/catch, shallow
 * structural validation, malformed state -> removeItem + null. Improvement
 * over that precedent: `saveOpenProgress` also wraps `setItem` in try/catch,
 * since quota errors / Safari private mode must never crash the app.
 */

// Canonical home for these two types is `frontend/src/api/satOpen.ts` — they
// mirror the backend's SatOpenQuestionDto / SatOpenDiagnosticResultDto.
// Re-exported here under the same names so existing imports of this module
// keep working.
export type { SatOpenQuestion, SatOpenDiagnosticResult } from '../api/satOpen';
import type { SatOpenDiagnosticForm, SatOpenQuestion, SatOpenDiagnosticResult } from '../api/satOpen';

export const SAT_OPEN_PROGRESS_KEY = 'satOpenProgress';

export interface OpenAnswerRecord {
  questionId: string;
  selectedKey: string | null; // null = answered-then-deselected (skip), mirrors the real player
  answeredAt: string; // ISO
  timeSpentSec?: number;
}

export interface OpenCheckResult {
  correct: boolean;
  correctKey: string;
  explanation: string;
}

export interface OpenPracticeSetRecord {
  id: string; // client-generated: `open-set-${crypto.randomUUID()}`
  domain: string | null;
  questions: SatOpenQuestion[]; // the answer-free DTOs, cached so the player works offline-ish
  answers: OpenAnswerRecord[];
  checks: Record<string, OpenCheckResult>; // keyed by questionId; filled per answer
  createdAt: string;
  completedAt: string | null;
  /** Question ids marked for review. Optional so older stored sets parse unchanged. */
  flagged?: string[];
}

export interface SatOpenProgressState {
  version: 1;
  startedAt: string;
  diagnostic: {
    status: 'not_started' | 'in_progress' | 'completed';
    answers: OpenAnswerRecord[];
    result: SatOpenDiagnosticResult | null;
    /**
     * The fetched diagnostic form, cached on first load (specs/009 Task 16).
     * Load-bearing, not an optimization: without it a reload mid-attempt would
     * draw a *different* set of questions and orphan the answers already given.
     * Optional so states written before this field parse unchanged.
     */
    form?: SatOpenDiagnosticForm | null;
    /** When Start was pressed. With the form's `timeLimitMin` this seeds the
     *  timer on resume. Optional for the same backward-compatibility reason. */
    startedAt?: string | null;
    /**
     * Active time spent on the in-progress attempt, in ms. The anonymous timer
     * only runs while the visitor is ON the page ("paused while away"), so the
     * clock is this accumulator, not wall time since `startedAt` — otherwise
     * coming back after an hour would auto-submit a blank attempt. Optional
     * (missing = 0) so states written before this field parse unchanged.
     */
    elapsedMs?: number;
    /** Question ids marked for review — survives a reload. Optional for the same reason. */
    flagged?: string[];
  };
  practice: { sets: OpenPracticeSetRecord[] };
}

export interface EvidenceEntry {
  questionId: string;
  selectedKey: string | null;
  answeredAt: string;
  activity: 'DIAGNOSTIC' | 'PRACTICE';
}

const DIAGNOSTIC_STATUSES = ['not_started', 'in_progress', 'completed'];

const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const everyHasQuestionId = (items: unknown): boolean =>
  Array.isArray(items) && items.every((item) => isObject(item) && typeof item.questionId === 'string');

/** Absent is fine (legacy states); present must be a list of strings. */
const isOptionalStringList = (value: unknown): boolean =>
  value === undefined || (Array.isArray(value) && value.every((item) => typeof item === 'string'));

function isValidPracticeSet(set: unknown): boolean {
  return (
    isObject(set) &&
    isOptionalStringList(set.flagged) &&
    typeof set.id === 'string' &&
    everyHasQuestionId(set.questions) &&
    everyHasQuestionId(set.answers) &&
    isObject(set.checks)
  );
}

/** Structural check deep enough that no consumer (`collectEvidence`,
 *  `seenQuestionIds`, the transports, the import) can hit a wrong-shaped row. */
function isValidState(value: unknown): value is SatOpenProgressState {
  if (!isObject(value)) return false;

  if (value.version !== 1) return false;
  if (typeof value.startedAt !== 'string') return false;

  const diagnostic = value.diagnostic;
  if (!isObject(diagnostic)) return false;
  if (typeof diagnostic.status !== 'string' || !DIAGNOSTIC_STATUSES.includes(diagnostic.status)) return false;
  if (!everyHasQuestionId(diagnostic.answers)) return false;
  if (!isOptionalStringList(diagnostic.flagged)) return false;

  const practice = value.practice;
  if (!isObject(practice)) return false;
  if (!Array.isArray(practice.sets) || !practice.sets.every(isValidPracticeSet)) return false;

  return true;
}

export function emptyOpenProgress(nowIso: string): SatOpenProgressState {
  return {
    version: 1,
    startedAt: nowIso,
    diagnostic: { status: 'not_started', answers: [], result: null },
    practice: { sets: [] },
  };
}

/** Returns null if there's no open progress yet, or if it exists but is
 * malformed (in which case it's cleared so a fresh session can start). */
export function loadOpenProgress(): SatOpenProgressState | null {
  if (unpersistedState) return unpersistedState;

  let raw: string | null;
  try {
    raw = localStorage.getItem(SAT_OPEN_PROGRESS_KEY);
  } catch {
    return null; // storage blocked (privacy settings / sandboxed frame)
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!isValidState(parsed)) {
      throw new Error('malformed open progress state');
    }
    return parsed;
  } catch {
    clearOpenProgress(); // guarded; a failed heal must not turn into a throw
    return null;
  }
}

/**
 * In-memory copy of the last state whose write FAILED (quota / private mode).
 * Without it a failed write is silent data loss: the next route reads storage,
 * finds nothing, and bounces the visitor home. It lives for the tab's SPA
 * lifetime only and is dropped the moment a write lands or the store is cleared.
 */
let unpersistedState: SatOpenProgressState | null = null;

/** Never throws — a full quota or Safari private-mode `setItem` failure must not
 * crash the app. Returns whether the write LANDED in storage; on `false` the
 * state is still served by `loadOpenProgress` for the rest of this tab session. */
export function saveOpenProgress(state: SatOpenProgressState): boolean {
  try {
    localStorage.setItem(SAT_OPEN_PROGRESS_KEY, JSON.stringify(state));
    unpersistedState = null;
    return true;
  } catch {
    unpersistedState = state;
    return false;
  }
}

/** Write probe: can this browser persist progress right now? Restores whatever
 * was under the probe key and never throws. */
export function canPersistOpenProgress(): boolean {
  const probeKey = `${SAT_OPEN_PROGRESS_KEY}:probe`;
  try {
    localStorage.setItem(probeKey, '1');
    localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

export function clearOpenProgress(): void {
  unpersistedState = null;
  try {
    localStorage.removeItem(SAT_OPEN_PROGRESS_KEY);
  } catch {
    // storage unavailable — nothing to clear
  }
}

/** Immutable upsert of a diagnostic answer, keyed by questionId. */
export function upsertDiagnosticAnswer(
  state: SatOpenProgressState,
  answer: OpenAnswerRecord
): SatOpenProgressState {
  const existingIndex = state.diagnostic.answers.findIndex((a) => a.questionId === answer.questionId);
  const answers =
    existingIndex === -1
      ? [...state.diagnostic.answers, answer]
      : state.diagnostic.answers.map((a, i) => (i === existingIndex ? answer : a));

  return {
    ...state,
    diagnostic: {
      ...state.diagnostic,
      answers,
    },
  };
}

/** Immutable replace of the diagnostic's marked-for-review list. */
export function setDiagnosticFlagged(
  state: SatOpenProgressState,
  questionIds: string[],
): SatOpenProgressState {
  return { ...state, diagnostic: { ...state.diagnostic, flagged: questionIds } };
}

/** Immutable replace of one practice set's marked-for-review list. Unknown set = no-op. */
export function setPracticeFlagged(
  state: SatOpenProgressState,
  setId: string,
  questionIds: string[],
): SatOpenProgressState {
  if (!state.practice.sets.some((set) => set.id === setId)) return state;
  return {
    ...state,
    practice: {
      sets: state.practice.sets.map((set) =>
        set.id === setId ? { ...set, flagged: questionIds } : set,
      ),
    },
  };
}

/** Merges diagnostic + practice answers into a single evidence stream, tagged
 * by which activity produced each answer. Order: diagnostic answers first (in
 * their stored order), then practice answers set-by-set. */
export function collectEvidence(state: SatOpenProgressState): EvidenceEntry[] {
  const diagnosticEvidence: EvidenceEntry[] = state.diagnostic.answers.map((a) => ({
    questionId: a.questionId,
    selectedKey: a.selectedKey,
    answeredAt: a.answeredAt,
    activity: 'DIAGNOSTIC',
  }));

  const practiceEvidence: EvidenceEntry[] = state.practice.sets.flatMap((set) =>
    set.answers.map((a) => ({
      questionId: a.questionId,
      selectedKey: a.selectedKey,
      answeredAt: a.answeredAt,
      activity: 'PRACTICE' as const,
    }))
  );

  return [...diagnosticEvidence, ...practiceEvidence];
}

/** All practice-set question ids ever served, unioned and deduplicated —
 * feeds excludeQuestionIds so a returning visitor doesn't get repeats. */
export function seenQuestionIds(state: SatOpenProgressState): string[] {
  const ids = new Set<string>();
  for (const set of state.practice.sets) {
    for (const question of set.questions) {
      ids.add(question.questionId);
    }
  }
  return [...ids];
}

export function completedActivityCount(state: SatOpenProgressState): number {
  const completedSets = state.practice.sets.filter((set) => set.completedAt !== null).length;
  return completedSets + (state.diagnostic.status === 'completed' ? 1 : 0);
}

export function hasAnyProgress(state: SatOpenProgressState | null): boolean {
  if (!state) return false;
  if (state.diagnostic.status !== 'not_started') return true;
  if (state.diagnostic.answers.length > 0) return true;
  if (state.practice.sets.length > 0) return true;
  return false;
}
